import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentArrowUpIcon,
  DocumentIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const DOCUMENT_TYPES = [
  'CNH',
  'RG',
  'Comprovante de Endereço',
  'Comprovante de Renda',
  'Contrato Social',
  'Cartão CNPJ',
  'Procuração',
  'Outro'
];

const ClientDocumentsTab = ({ clientId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const [blobUrls, setBlobUrls] = useState({});
  const [loadingBlob, setLoadingBlob] = useState(false);

  const [uploadData, setUploadData] = useState({
    file: null,
    documentType: 'Outro',
    description: ''
  });

  useEffect(() => {
    if (clientId) loadDocuments();
  }, [clientId]);

  const selectedDocument = viewerIndex >= 0 ? documents[viewerIndex] : null;

  useEffect(() => {
    if (!selectedDocument) return;
    if (blobUrls[selectedDocument.id]) return;
    if (!isViewable(selectedDocument.mime_type)) return;
    let cancelled = false;
    setLoadingBlob(true);
    api.get(`/api/client-documents/view/${selectedDocument.id}`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setBlobUrls(prev => ({ ...prev, [selectedDocument.id]: url }));
      })
      .catch(() => toast.error('Erro ao carregar documento'))
      .finally(() => { if (!cancelled) setLoadingBlob(false); });
    return () => { cancelled = true; };
  }, [selectedDocument?.id]);

  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (viewerIndex < 0) return;
    if (e.key === 'ArrowLeft') setViewerIndex(i => (i > 0 ? i - 1 : documents.length - 1));
    if (e.key === 'ArrowRight') setViewerIndex(i => (i < documents.length - 1 ? i + 1 : 0));
    if (e.key === 'Escape') setViewerIndex(-1);
  }, [viewerIndex, documents.length]);

  useEffect(() => {
    if (viewerIndex >= 0) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [viewerIndex, handleKeyDown]);

  const loadDocuments = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await api.get(`/api/client-documents/client/${clientId}`);
      if (response.data.success) setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Arquivo muito grande! Máximo: 15MB');
      return;
    }
    setUploadData({ ...uploadData, file });
  };

  const handleUpload = async () => {
    if (!uploadData.file) { toast.error('Selecione um arquivo'); return; }
    if (!uploadData.documentType) { toast.error('Selecione o tipo de documento'); return; }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('clientId', clientId);
      formData.append('documentType', uploadData.documentType);
      formData.append('description', uploadData.description);

      const response = await api.post('/api/client-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Documento enviado com sucesso!');
        setShowUploadModal(false);
        setUploadData({ file: null, documentType: 'Outro', description: '' });
        loadDocuments();
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error.response?.data?.error || 'Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/api/client-documents/download/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    } catch (error) {
      toast.error('Erro ao fazer download');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Tem certeza que deseja deletar este documento?')) return;
    try {
      const response = await api.delete(`/api/client-documents/${documentId}`);
      if (response.data.success) {
        toast.success('Documento deletado!');
        if (viewerIndex >= 0) setViewerIndex(-1);
        loadDocuments();
      }
    } catch (error) {
      toast.error('Erro ao deletar documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isViewable = (mime) => {
    if (!mime) return false;
    return mime.startsWith('image/') || mime === 'application/pdf';
  };

  const getTypeIcon = (mime) => {
    if (!mime) return 'text-gray-500';
    if (mime.startsWith('image/')) return 'text-green-500';
    if (mime === 'application/pdf') return 'text-red-500';
    return 'text-blue-500';
  };

  if (!clientId) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-yellow-800 dark:text-yellow-200 text-sm">
          Salve o cliente primeiro para poder adicionar documentos
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Documentos do Cliente</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            CNH, comprovantes, contrato social e outros ({documents.length})
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
          Adicionar
        </button>
      </div>

      {/* Grid de documentos */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Carregando...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Nenhum documento cadastrado</p>
          <p className="text-xs text-gray-400 mt-1">Clique em "Adicionar" para enviar documentos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {documents.map((doc, idx) => (
            <div
              key={doc.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => isViewable(doc.mime_type) && setViewerIndex(idx)}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 p-2 rounded-lg bg-gray-100 dark:bg-gray-700 ${getTypeIcon(doc.mime_type)}`}>
                  <DocumentIcon className="h-8 w-8" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.file_name}</h4>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {doc.document_type}
                  </span>
                  {doc.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{doc.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isViewable(doc.mime_type) && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setViewerIndex(idx); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded" title="Visualizar">
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                    className="p-1.5 text-gray-400 hover:text-green-600 rounded" title="Download">
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Deletar">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Adicionar Documento</h3>
              <button type="button" onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Documento *</label>
                <select
                  value={uploadData.documentType}
                  onChange={(e) => setUploadData({ ...uploadData, documentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Arquivo *</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {uploadData.file && (
                  <p className="text-xs text-gray-500 mt-1">
                    {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                    {uploadData.file.type.startsWith('image/') && (
                      <span className="text-green-600 ml-1">- sera comprimido automaticamente</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descricao (opcional)</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: CNH vencimento 2027..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setShowUploadModal(false)} disabled={uploading}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                  Cancelar
                </button>
                <button type="button" onClick={handleUpload} disabled={uploading || !uploadData.file}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                  {uploading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Enviando...</>
                  ) : (
                    <><DocumentArrowUpIcon className="h-5 w-5 mr-2" />Enviar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Viewer Expandido com Navegacao */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/95 z-[70] flex flex-col"
          onClick={() => setViewerIndex(-1)}>

          {/* Header do viewer */}
          <div className="flex items-center justify-between px-6 py-3 bg-gray-900 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate text-lg">{selectedDocument.file_name}</h3>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white">{selectedDocument.document_type}</span>
                <span className="text-xs text-gray-400">{formatFileSize(selectedDocument.file_size)}</span>
                <span className="text-sm text-gray-300 font-medium">{viewerIndex + 1} / {documents.length}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <button type="button" onClick={() => handleDownload(selectedDocument)}
                className="p-2 text-gray-300 hover:text-white rounded-lg hover:bg-white/10" title="Download">
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => handleDelete(selectedDocument.id)}
                className="p-2 text-gray-300 hover:text-red-400 rounded-lg hover:bg-white/10" title="Deletar">
                <TrashIcon className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setViewerIndex(-1)}
                className="p-2 text-gray-300 hover:text-white rounded-lg hover:bg-white/10" title="Fechar (Esc)">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Area de visualizacao - tela cheia */}
          <div className="flex-1 relative min-h-0" onClick={(e) => e.stopPropagation()}>
            {/* Seta Esquerda */}
            {documents.length > 1 && (
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
                onClick={() => setViewerIndex(i => (i > 0 ? i - 1 : documents.length - 1))}
                title="Anterior"
              >
                <ChevronLeftIcon className="h-8 w-8" />
              </button>
            )}

            {/* Seta Direita */}
            {documents.length > 1 && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
                onClick={() => setViewerIndex(i => (i < documents.length - 1 ? i + 1 : 0))}
                title="Proximo"
              >
                <ChevronRightIcon className="h-8 w-8" />
              </button>
            )}

            {/* Conteudo */}
            <div className="h-full w-full px-16">
              {loadingBlob && !blobUrls[selectedDocument.id] ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              ) : selectedDocument.mime_type?.startsWith('image/') ? (
                <div className="h-full flex items-center justify-center p-4">
                  <img
                    src={blobUrls[selectedDocument.id] || ''}
                    alt={selectedDocument.file_name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : selectedDocument.mime_type === 'application/pdf' ? (
                <iframe
                  src={blobUrls[selectedDocument.id] || ''}
                  className="w-full h-full border-0"
                  title={selectedDocument.file_name}
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                  <DocumentIcon className="h-16 w-16 text-gray-500 mb-4" />
                  <p className="text-gray-400 mb-4">Pre-visualizacao nao disponivel ({selectedDocument.mime_type})</p>
                  <button type="button" onClick={() => handleDownload(selectedDocument)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />Fazer Download
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Dots de navegacao */}
          {documents.length > 1 && (
            <div className="flex justify-center py-3 bg-gray-900 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-2 px-4 py-1">
                {documents.map((doc, idx) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => setViewerIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === viewerIndex ? 'bg-blue-500 scale-125' : 'bg-gray-500 hover:bg-gray-300'
                    }`}
                    title={doc.file_name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientDocumentsTab;
